import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api/client';
import { isApiError } from '@/lib/api/errors';
import { usePurchasingAuthKey } from './api';

export type CardStatus = 'UNMATCHED' | 'MATCHED' | 'IGNORED' | 'PERSONAL' | 'DISPUTED';

export const cardStatusLabels: Record<CardStatus, string> = {
  UNMATCHED: 'Unmatched',
  MATCHED: 'Matched',
  PERSONAL: 'Personal',
  DISPUTED: 'Disputed',
  IGNORED: 'Ignored',
};

/** A card charge (PUR-CON-40). Linked totals are absent when costs are hidden. */
export interface CardTransaction {
  id: string;
  version: number;
  status: CardStatus;
  source: 'CSV_IMPORT' | 'ACCOUNTING_BANK_FEED' | 'ISSUING';
  cardLast4: string;
  cardAccount: { id: string; name: string; last4: string } | null;
  cardholderId: string | null;
  cardholder: { id: string; displayName: string } | null;
  merchantName: string;
  mcc: string | null;
  amount: number;
  currency: string;
  postedAt: string;
  authorizedAt: string | null;
  reference: string;
  purchaseOrderId: string | null;
  purchaseOrder: { id: string; orderNumber: string; vendorName: string; total?: number } | null;
  expenseId: string | null;
  expense: { id: string; name: string; vendor: string | null; total?: number } | null;
  supplierBillId: string | null;
  supplierBill: {
    id: string;
    number: string;
    supplierInvoiceNumber: string;
    supplierName: string;
    total?: number;
  } | null;
  jobId: string | null;
  job: { id: string; jobNumber: string; name: string } | null;
  businessUnitId: string | null;
  businessUnit: { id: string; name: string } | null;
  glAccount: string | null;
  note: string | null;
  matchedAt: string | null;
  matchedBy: string | null;
}

export interface CardTransactionPage {
  items: CardTransaction[];
  nextCursor: string | null;
  counts: Partial<Record<CardStatus, number>>;
}

export interface CardAccount {
  id: string;
  version: number;
  name: string;
  last4: string;
  cardholderId: string | null;
  cardholder: { id: string; displayName: string } | null;
  bankFeedImportsCharges: boolean;
  glAccount: string | null;
  isActive: boolean;
}

export type CardAccountInput = {
  version?: number;
  name: string;
  last4: string;
  cardholderId: string | null;
  bankFeedImportsCharges: boolean;
  glAccount: string | null;
  isActive: boolean;
};

export interface CardCandidates {
  purchaseOrders: Array<{
    id: string;
    orderNumber: string;
    vendorName: string;
    total?: number;
    orderedAt: string | null;
    job: { id: string; jobNumber: string; name: string } | null;
  }>;
  expenses: Array<{
    id: string;
    name: string;
    vendor: string | null;
    total?: number;
    date: string;
  }>;
}

export interface CardImportJob {
  jobId: string;
  kind?: 'import' | 'rematch';
  status: 'queued' | 'waiting' | 'running' | 'paused' | 'done' | 'failed';
  total: number;
  processed: number;
  imported: number;
  skipped: number;
  matched: number;
  reason?: string;
  result?: {
    imported: number;
    skipped: number;
    matched: number;
    errors: Array<{ row: number; message: string }>;
  };
}

const base = '/purchasing/card-transactions';

export function useCardTransactions(filters: { status?: CardStatus; q?: string }) {
  const authKey = usePurchasingAuthKey();
  return useInfiniteQuery({
    queryKey: ['card-transactions', ...authKey, filters],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      apiGet<CardTransactionPage>(operationsClient, base, {
        params: {
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.q?.trim() ? { q: filters.q.trim() } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
          limit: 50,
        },
      }),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });
}

export function useCardCandidates(id: string | null) {
  const authKey = usePurchasingAuthKey();
  return useQuery({
    queryKey: ['card-transactions', ...authKey, 'candidates', id],
    enabled: !!id,
    queryFn: () => apiGet<CardCandidates>(operationsClient, `${base}/${id}/candidates`),
  });
}

export function useCardAccounts() {
  const authKey = usePurchasingAuthKey();
  return useQuery({
    queryKey: ['card-accounts', ...authKey],
    queryFn: () => apiGet<CardAccount[]>(operationsClient, '/purchasing/card-accounts'),
  });
}

export function useCardholders(enabled: boolean) {
  const authKey = usePurchasingAuthKey();
  return useQuery({
    queryKey: ['card-accounts', ...authKey, 'cardholders'],
    enabled,
    queryFn: () =>
      apiGet<Array<{ id: string; displayName: string }>>(
        operationsClient,
        '/purchasing/card-accounts/cardholders',
      ),
  });
}

/** Every change to a charge refreshes the list and its counts. */
export function useCardAction() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      action,
      body,
    }: {
      id: string;
      action: 'match' | 'unmatch' | 'status' | 'code';
      body: Record<string, unknown>;
    }) =>
      action === 'code'
        ? apiPatch<CardTransaction>(operationsClient, `${base}/${id}`, body)
        : apiPost<CardTransaction>(operationsClient, `${base}/${id}/${action}`, body),
    onSuccess: () => void cache.invalidateQueries({ queryKey: ['card-transactions'] }),
    onError: (error) =>
      toast.error(isApiError(error) ? error.message : 'Could not update the charge'),
  });
}

export function useSaveCardAccount() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: CardAccountInput }) =>
      id
        ? apiPatch<CardAccount>(operationsClient, `/purchasing/card-accounts/${id}`, input)
        : apiPost<CardAccount>(operationsClient, '/purchasing/card-accounts', input),
    onSuccess: () => void cache.invalidateQueries({ queryKey: ['card-accounts'] }),
  });
}

export const previewStatement = (rows: unknown[], cardAccountId?: string) =>
  apiPost<{ valid: number; duplicates: number; errors: Array<{ row: number; message: string }> }>(
    operationsClient,
    `${base}/import/preview`,
    { rows, ...(cardAccountId ? { cardAccountId } : {}) },
  );

export const startStatementImport = (rows: unknown[], cardAccountId?: string) =>
  apiPost<CardImportJob>(operationsClient, `${base}/import/jobs`, {
    rows,
    ...(cardAccountId ? { cardAccountId } : {}),
  });

export const startRematch = () =>
  apiPost<CardImportJob>(operationsClient, `${base}/rematch/jobs`, {});

export function useCardJob(jobId: string | null) {
  const cache = useQueryClient();
  return useQuery({
    // Not under 'card-transactions': a finished job refreshes that list.
    queryKey: ['card-import-job', jobId],
    enabled: !!jobId,
    queryFn: async () => {
      const job = await apiGet<CardImportJob>(operationsClient, `${base}/jobs/${jobId}`);
      if (job.status === 'done') void cache.invalidateQueries({ queryKey: ['card-transactions'] });
      return job;
    },
    refetchInterval: (query) =>
      query.state.data && ['done', 'failed'].includes(query.state.data.status) ? false : 2000,
  });
}
